import axios, { AxiosError, AxiosRequestConfig } from "axios"
import { tokenService } from "./token.service"
import { APP_API_URL } from './api-config.service'

axios.defaults.withCredentials = true

interface CustomRequestConfig extends AxiosRequestConfig {
    _retryCount?: number
    _authRetry?: boolean
}

const REQUEST_TIMEOUT_MS = 30000
const MAX_RETRY_COUNT = 2
const RETRYABLE_METHODS = new Set(["get", "head", "options"])
const RETRYABLE_STATUS_CODES = new Set([408, 425, 429, 500, 502, 503, 504])

const api = axios.create({
    baseURL: APP_API_URL,
    withCredentials: true,
    timeout: REQUEST_TIMEOUT_MS,
})

const getToken = () => localStorage.getItem("us_id")

const waitForRetry = (delayMs: number, signal?: AbortSignal) => new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
        reject(new axios.CanceledError())
        return
    }

    const handleAbort = () => {
        clearTimeout(timeoutID)
        reject(new axios.CanceledError())
    }
    const timeoutID = setTimeout(() => {
        signal?.removeEventListener("abort", handleAbort)
        resolve()
    }, delayMs)

    signal?.addEventListener("abort", handleAbort, { once: true })
})

const isRetryableRequest = (request: CustomRequestConfig, error: AxiosError) => {
    if (request.signal?.aborted) return false

    const method = (request.method || "get").toLowerCase()
    if (!RETRYABLE_METHODS.has(method)) return false

    const status = error.response?.status
    if (status) return RETRYABLE_STATUS_CODES.has(status)

    return ["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"].includes(error.code || "")
}

// --- Retry для сетевых ошибок ---
const retryRequest = async (
    originalRequest: CustomRequestConfig,
    error: AxiosError
) => {
    originalRequest._retryCount = originalRequest._retryCount || 0

    if (originalRequest._retryCount >= MAX_RETRY_COUNT) {
        return Promise.reject(error)
    }

    originalRequest._retryCount += 1

    const retryDelayMs = 300 * Math.pow(2, originalRequest._retryCount - 1) + Math.floor(Math.random() * 200)
    await waitForRetry(retryDelayMs, originalRequest.signal as AbortSignal | undefined)

    return api.request(originalRequest)
}

// --- Interceptors ---
api.interceptors.request.use(
    (config) => {
        config.headers = config.headers || {}
        const token = getToken()
        if (token) config.headers.Authorization = `Bearer ${token}`
        return config
    },
    (error) => Promise.reject(error)
)

api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError & { config?: CustomRequestConfig }) => {
        const originalRequest = error.config

        if (originalRequest && isRetryableRequest(originalRequest, error)) {
            return retryRequest(originalRequest, error)
        }

        if (error.response?.status === 401 && originalRequest) {
            if (originalRequest._authRetry) {
                tokenService.clearTokensAndRedirect()
                return Promise.reject(error)
            }

            const isRefreshRequest = originalRequest.url?.includes('/auth/refresh')

            if (isRefreshRequest) {
                tokenService.clearTokensAndRedirect()
                return Promise.reject(error)
            }

            originalRequest._authRetry = true
            const refreshed = await tokenService.refreshTokenRequest()

            if (!refreshed) {
                tokenService.clearTokensAndRedirect()
                return Promise.reject(error)
            }

            if (refreshed) return api.request(originalRequest)
        }

        return Promise.reject(error)
    }
)

export default api